import { useCallback } from 'react';
import { useParams } from 'react-router-dom';

import { PalmDataGrid } from 'common/components/PalmDataGrid';
import { Roles } from 'common/constants/roles';
import { Routes } from 'common/constants/routes';
import { usePalmButtonEvaluator } from 'common/hooks/usePalmButtonEvaluator';
import { usePalmTabState } from 'common/hooks/usePalmTabState';
import { fetchUserGroupUserMembershipList } from 'common/services/api/UserGroupUserMembership/UserGroupUserMembership.thunk';
import { ActionDefinition, ColumnDefinition, ColumnType, DrawerEntitiesNames,Operations } from 'common/types/TypeDefs';

import { UserGroupUserMembershipCreateContent } from './UserGroupUserMembershipCreateDrawerContent';
import { UserGroupUserMembershipDeleteDialog } from './UserGroupUserMembershipDeleteDialog';
import { addNewFilter, clearFilters, removeFilter, selectFilters } from './userGroupUserMembershipSlice';

export function UserGroupUserMembershipTab() {
    const typeName = 'User Group';
    const { suiteId = '', tenantId ='', userId = '' } = useParams();

    const shouldDisableButtons = usePalmButtonEvaluator([ Roles.Admin, Roles.MultiTenantRO, Roles.AppManager, Roles.UserManager ]);
    const shouldDisableButtonsForCreate = usePalmButtonEvaluator([ Roles.Admin, Roles.UserManager ]);
    const shouldDisableDeleteOperation = usePalmButtonEvaluator([ Roles.Admin, Roles.UserManager ]);

    const { processing, sortModel, setSortModel, filterProp, fetchEntities } = usePalmTabState({
        typeName,
        fetchParams: { tenantId, userId },
        fetchThunk: fetchUserGroupUserMembershipList,
        filterActions: { selectFilters, addNewFilter, removeFilter, clearFilters }
    });

    const onGetRowLink = useCallback((userGroupId: string) => {
        return `/${Routes.ApplicationSuites}/${suiteId}/${Routes.Tenants}/${tenantId}/${Routes.UserGroups}/${userGroupId}/${Routes.Users}`;
    }, []);

    const actionDefinitions =  [
        { action: Operations.Open, disabled: shouldDisableButtons, urlToOpen: onGetRowLink },
        { action: Operations.Delete, disabled: shouldDisableDeleteOperation }
    ] as ActionDefinition[];

    const columnDefinitions: ColumnDefinition[] = [
        { columnId: 'userGroupId', columnName: 'User Group ID', columnType: ColumnType.First, copy: true, shouldFilterByExactMatch: true },
        { columnId: 'name', columnName: 'User Group Name', columnType: ColumnType.Tooltip, copy: false },
        { columnId: 'description', columnName: 'User Group Description', columnType: ColumnType.Tooltip, copy: false },
        { columnId: 'actions', columnName: 'Action', columnType: ColumnType.Action }
    ];

    return <PalmDataGrid
        title={typeName}
        processing={processing}
        actionDefinitions={actionDefinitions}
        columnDefinitions={columnDefinitions}
        contentFunction={UserGroupUserMembershipCreateContent}
        customDeleteTitle={'Are you sure you want to remove this User from this User Group?'}
        operationName={Operations.Link}
        isCreateButtonDisabled={shouldDisableButtonsForCreate}
        deleteFunction={UserGroupUserMembershipDeleteDialog}
        filter={filterProp}
        refreshDataGridClick={() => fetchEntities()}
        getRowLink={shouldDisableButtons ? undefined : onGetRowLink}
        currentEntityName={DrawerEntitiesNames.AppSuiteUserGroup}
        sortModel={sortModel}
        clientSortHandler={setSortModel}
        shouldShowExportButton={true}
    />;
}
